// components/auth/login-form.tsx
// Spec 0018, AC-6: this used to wire up a full email/password form (schema,
// submit handler, two factor state, a stored-email localStorage cache) but
// never actually rendered any input field inside the <form> — none of that
// logic could ever run. Real sign in is Microsoft SSO only (CardWrapper's
// showSocial renders the actual working button via components/form-ui/social.tsx);
// this is now just that shell, with the dead credentials internals removed.
"use client";

import { CardWrapper } from "@/components/form-ui/card-wrapper";
import { FormError } from "@/components/form-ui/form-error";
import { EmployeeIdLogin } from "@/components/auth/employee-id-login";
import { LoginHeroPanel } from "@/components/auth/login-hero-panel";
import { LanguageType } from "@/lib/types";
import { _t } from "@/lib/utils";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

interface LoginFormProps {
  language: LanguageType;
}

export const LoginForm = ({ language }: LoginFormProps) => {
  const searchParams = useSearchParams();
  const urlError =
    searchParams.get("error") === "OAuthAccountNotLinked"
      ? "Error already in use with different provider!"
      : "";

  // Live feedback: once someone opens the employee-number flow, showing
  // "Login using email account" plus a whole separate Microsoft button
  // underneath it no longer makes sense -- they've already picked their
  // path. Hide Microsoft and swap the header while that flow is active.
  const [employeeFlowExpanded, setEmployeeFlowExpanded] = useState(false);

  return (
    // No overflow-hidden here on purpose: it clipped real content (focus
    // rings, hover states) whenever the card's own box ran even slightly
    // tight. Each side rounds only its own outer corner instead (logical
    // start/end, so this still flips correctly in Arabic) -- looks like
    // one seamless card with nothing ever cropped.
    <div className="group w-full max-w-4xl mx-4 flex flex-col md:flex-row items-stretch shadow-2xl rounded-3xl">
      <LoginHeroPanel language={language} />
      {/* CardWrapper's own Card fills this column edge to edge (no
          leftover gap, no centered-fixed-width box) -- the readable
          content inside gets its own max-width wrapper below instead, so
          the card's white background still touches the hero panel with
          zero seam while the form itself stays a comfortable width. */}
      <CardWrapper
        language={language}
        classNames="dark:bg-[#252F3F] dark:border-[#3E4C61] flex-1 w-full flex flex-col justify-center border-0 shadow-none rounded-3xl md:rounded-s-none md:rounded-e-3xl"
        headerLabel=""
        backButtonHref="/"
        showSocial={!employeeFlowExpanded}
      >
        <div className="w-full max-w-[300px] mx-auto">
          <div className="text-center -mt-2 mb-5">
            <h2
              className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {_t(employeeFlowExpanded ? "employee_login_entry_point" : "login_welcome_heading", language)}
            </h2>
            {!employeeFlowExpanded && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                {_t("login_welcome_subtext", language)}
              </p>
            )}
          </div>
          <FormError message={urlError} />
          {/* Spec 0022, AC-3: a secondary, collapsed entry point below Microsoft
              SSO (the primary path for everyone else). */}
          <div className="mt-4 flex w-full justify-center">
            <EmployeeIdLogin language={language} onExpandedChange={setEmployeeFlowExpanded} />
          </div>
        </div>
      </CardWrapper>
    </div>
  );
};
